import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import p1 from '../assets/images/1.jpeg';
import p2 from '../assets/images/2.jpeg';
import p3 from '../assets/images/3.jpeg';
import p4 from '../assets/images/4.jpeg';
import p5 from '../assets/images/5.jpeg';
import p6 from '../assets/images/6.jpg';
import p7 from '../assets/images/7.jpeg';
import p8 from '../assets/images/8.jpeg';

export default function Portfolio() {
  const items = useMemo(
    () => [
      { src: p1, caption: '01' },
      { src: p2, caption: '02' },
      { src: p3, caption: '03' },
      { src: p4, caption: '04' },
      { src: p5, caption: '05' },
      { src: p6, caption: '06' },
      { src: p7, caption: '07' },
      { src: p8, caption: '08' },
    ],
    []
  );

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isOpen = lightboxIndex !== null;
  const active = isOpen ? items[lightboxIndex] : null;

  const [zoom, setZoom] = useState(1);

  const openAt = useCallback((idx) => {
    setLightboxIndex(idx);
    setZoom(1);
  }, []);

  const close = useCallback(() => {
    setLightboxIndex(null);
    setZoom(1);
  }, []);

  const prev = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null) return i;
      return (i - 1 + items.length) % items.length;
    });
    setZoom(1);
  }, [items.length]);

  const next = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null) return i;
      return (i + 1) % items.length;
    });
    setZoom(1);
  }, [items.length]);

  const goTo = useCallback((idx) => {
    setLightboxIndex(idx);
    setZoom(1);
  }, []);

  const toggleZoom = useCallback(() => {
    setZoom((z) => (z > 1 ? 1 : 1.8));
  }, []);

  // ✅ Keyboard + lock scroll
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)));
      if (e.key === '-') setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)));
      if (e.key.toLowerCase() === 'z') toggleZoom();
    };

    document.addEventListener('keydown', onKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, close, prev, next, toggleZoom]);

  // ✅ Swipe (mobile)
  const gestureRef = useRef({ startX: 0, startY: 0, lastTap: 0 });

  const onTouchStart = useCallback(
    (e) => {
      const t = e.touches?.[0];
      if (!t) return;

      gestureRef.current.startX = t.clientX;
      gestureRef.current.startY = t.clientY;

      // double tap zoom
      const now = Date.now();
      const dt = now - gestureRef.current.lastTap;
      gestureRef.current.lastTap = now;
      if (dt > 0 && dt < 280) toggleZoom();
    },
    [toggleZoom]
  );

  const onTouchEnd = useCallback(
    (e) => {
      const t = e.changedTouches?.[0];
      if (!t) return;

      const dx = t.clientX - gestureRef.current.startX;
      const dy = t.clientY - gestureRef.current.startY;

      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        if (dx > 0) prev();
        else next();
      }
    },
    [prev, next]
  );

  // ✅ Wheel zoom (PC)
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY;
    setZoom((z) => {
      const nextZ = delta > 0 ? z - 0.12 : z + 0.12;
      return Math.min(4, Math.max(1, +nextZ.toFixed(2)));
    });
  }, []);

  return (
    <section className="py-32 relative bg-[#F8FAFC] dark:bg-slate-950 overflow-hidden" id="portfolio">
      {/* ...tu contenido anterior (header, stats, bento) sin cambios... */}

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* ✅ Masonry Gallery */}
        <div className="mt-24">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
                            <h3 className="text-3xl md:text-4xl font-display font-black text-secondary dark:text-slate-100 tracking-tight">
               Galería de Proyectos
              </h3>
            </div>

            <p className="text-slate-500 dark:text-slate-300 text-sm font-light max-w-md text-right">
              Click para abrir · Swipe para cambiar · Rueda para zoom.
            </p>
          </div>

          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6">
            {items.map((item, idx) => (
              <figure
                key={idx}
                className="
                  group mb-6 break-inside-avoid overflow-hidden rounded-2xl
                  bg-white/60 dark:bg-slate-950/40
                  border border-white/40 dark:border-white/10
                  shadow-[0_14px_40px_rgba(2,6,23,0.10)]
                  transition-transform duration-300 hover:-translate-y-1
                "
              >
                <button
                  type="button"
                  onClick={() => openAt(idx)}
                  className="relative block w-full text-left"
                  aria-label={`Abrir ${item.caption ?? `Proyecto ${idx + 1}`}`}
                >
                  <img src={item.src} alt={item.caption ?? `Proyecto ${idx + 1}`} loading="lazy" className="w-full h-auto object-cover" />
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </figure>
            ))}
          </div>
        </div>
      </div>

      {/* ✅ Lightbox Modal */}
      {isOpen && active ? (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Vista previa de imagen"
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
          // ✅ CAPTURAMOS LA RUEDA AQUÍ (no en un div por debajo),
          // así no creamos una capa invisible que tape tus botones (tu bug del recuadro rojo).
          onWheel={onWheel}
        >
          {/* Top bar (con z-40 para estar encima de todo) */}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between gap-4 z-40">
            <div className="text-white/90 text-sm font-semibold truncate">
              {active.caption ?? `${lightboxIndex + 1}`}{' '}
              <span className="text-white/60">
                ({lightboxIndex + 1}/{items.length})
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoom((z) => Math.max(1, +(z - 0.2).toFixed(2)));
                }}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition"
                aria-label="Alejar"
                title="Alejar (-)"
              >
                <span className="material-symbols-outlined">remove</span>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)));
                }}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition"
                aria-label="Acercar"
                title="Acercar (+)"
              >
                <span className="material-symbols-outlined">add</span>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleZoom();
                }}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition"
                aria-label="Zoom"
                title="Zoom (Z)"
              >
                <span className="material-symbols-outlined">zoom_in</span>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                }}
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition"
                aria-label="Cerrar"
                title="Cerrar (Esc)"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

          {/* Prev / Next (z-40 también) */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition z-40"
            aria-label="Anterior"
            title="Anterior (←)"
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white flex items-center justify-center transition z-40"
            aria-label="Siguiente"
            title="Siguiente (→)"
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>

          {/* Image stage */}
          <div
            className="absolute inset-0 flex items-center justify-center px-6 pt-20 pb-28 z-10"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            style={{ touchAction: 'pan-y' }}
          >
            <img
              src={active.src}
              alt={active.caption ?? `${lightboxIndex + 1}`}
              draggable={false}
              className="max-h-full max-w-full object-contain rounded-2xl shadow-[0_30px_90px_rgba(0,0,0,0.55)] transition-transform duration-200 select-none pointer-events-none"
              style={{ transform: `scale(${zoom})` }}
            />
          </div>

          {/* Thumbnails (z-40) */}
          <div className="absolute bottom-0 left-0 right-0 pb-4 z-40">
            <div className="mx-auto max-w-5xl px-6">
              <div className="flex gap-2 overflow-x-auto py-2">
                {items.map((it, idx) => {
                  const selected = idx === lightboxIndex;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goTo(idx);
                      }}
                      className={[
                        'pointer-events-auto relative flex-shrink-0 rounded-xl overflow-hidden border transition',
                        selected
                          ? 'border-lime-400 shadow-[0_0_0_1px_rgba(163,230,53,0.6),0_0_24px_rgba(163,230,53,0.25)]'
                          : 'border-white/15 hover:border-white/30',
                      ].join(' ')}
                      aria-label={`Ir a ${it.caption ?? `${idx + 1}`}`}
                      title={it.caption ?? `${idx + 1}`}
                    >
                      <img src={it.src} alt="" className="h-14 w-20 object-cover opacity-90" draggable={false} />
                      {selected ? <span className="absolute inset-0 bg-lime-400/10" /> : null}
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 flex justify-center">
                <div className="text-[11px] text-white/70 px-4 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md">
                  ESC cerrar · ← → navegar · Rueda zoom · Z zoom
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}